<script setup lang="ts">
import { computed, ref } from 'vue'
import { AccessPointsUtil, type AccessPointLevel } from '@/util/access_points'
import { announce } from '@/util/live_announcer'
import { pathwaysUrls } from '@/util/links'
import { capitalize } from '@/util/strings'
import RemovableList from './RemovableList.vue'

export interface SelectedResource {
  title?: string
  language?: string
}

const props = withDefaults(defineProps<{
  id: string
  selected?: string[]
  resourcesMap?: Record<string, SelectedResource>
  apLevel: AccessPointLevel
  objLevel?: string
  goalType?: string
}>(), {
  selected: () => [],
  resourcesMap: () => ({}),
  objLevel: undefined,
  goalType: 'annual_target'
})

// The languages travel with adds and removes because the page saves them alongside the
// resource list. A reorder sends none, as in Vue 2, so it can't overwrite them from a stale map.
const emit = defineEmits<{
  'update:selected': [uuids: string[], languages?: string[]]
  add: [uuid: string, languages: string[]]
}>()

const uuidToAdd = ref('')

const goalTypeLabel = computed(() => props.goalType !== 'annual_target' ? `${props.objLevel} Quarter` : 'Annual Target')

const titleFor = (uuid: string) => props.resourcesMap[uuid]?.title || uuid

const nonEnglishLanguage = (uuid: string) => {
  const language = props.resourcesMap[uuid]?.language
  return language && language !== 'english' ? capitalize(language) : ''
}

// Same shape as Vue 2: languages of the listed resources the map knows, in map order, blanks left out.
function languagesOf(uuids: string[]): string[] {
  const languages = Object.entries(props.resourcesMap)
    .filter(([uuid, resource]) => uuids.includes(uuid) && resource.language !== '')
    .map(([, resource]) => resource.language as string)
  return [...new Set(languages)]
}

function emitSelection(uuids: string[]) {
  emit('update:selected', uuids, languagesOf(uuids))
}

function addResource() {
  const uuid = uuidToAdd.value.trim()
  uuidToAdd.value = ''
  if (!uuid || props.selected.includes(uuid)) return
  const uuids = [...props.selected, uuid]
  emitSelection(uuids)
  emit('add', uuid, languagesOf(uuids))
  announce(`Added resource ${uuid}`)
}
</script>

<template>
  <div class="mb-4">
    <label :for="id" class="block mb-1 font-bold">
      {{ goalTypeLabel }} Resources | {{ AccessPointsUtil.labelsMap[apLevel] }}
    </label>
    <div class="flex">
      <input
        :id="id"
        v-model="uuidToAdd"
        type="text"
        placeholder="Paste resource UUID"
        class="grow h-10 px-3 text-md text-gray-900 bg-white border border-gray-600 rounded-l-xs placeholder:text-gray-700 focus:outline-primary-400"
        @keydown.enter.prevent="addResource"
      >
      <button
        type="button"
        class="h-10 px-4 text-md text-gray-900 bg-white border border-l-0 border-gray-600 rounded-r-xs hover:bg-gray-300 disabled:text-gray-700 disabled:cursor-not-allowed focus-visible:outline-primary-400"
        :disabled="!uuidToAdd"
        @click="addResource"
      >
        Add
      </button>
    </div>
    <RemovableList
      :items="selected"
      :item-key="uuid => uuid"
      :item-label="titleFor"
      noun="resource"
      :empty-focus-id="id"
      reorderable
      @remove="index => emitSelection(selected.filter((_, i) => i !== index))"
      @reorder="uuids => emit('update:selected', uuids)"
    >
      <template #item="{ item: uuid }">
        <a :href="pathwaysUrls.resourceDetail(uuid)" target="_blank" class="text-primary-400 underline">{{ titleFor(uuid) }}</a>
        <span v-if="nonEnglishLanguage(uuid)" class="ml-2 px-2 py-0.5 text-sm text-white bg-gray-700 rounded-full">
          {{ nonEnglishLanguage(uuid) }}
        </span>
      </template>
    </RemovableList>
  </div>
</template>
